import {ContentHeader} from "./ContentHeader";
import {RoleHeader} from "./RoleHeader";
import {Funnel} from "./Funnel";
import {array_move} from "../utils/functions";

type AnyHeader = Funnel | ContentHeader | RoleHeader;

export abstract class HeadersCommon<T extends AnyHeader> {

    protected any_headers: T[];

    constructor(headers: T[]) {
        this.any_headers = headers;
    }

    protected abstract cloneHeader(original: T): T;

    get FirstPosition(): number {
        return 0;
    }

    get LastPosition(): number {
        return this.Length;
    }

    get Length(): number {
        return this.any_headers.length;
    }

    get Exists() {
        return 0 < this.Length
    }

    get FirstHeader(): T {
        if (this.Length === 0) {
            throw new Error('no header');
        }
        return this.any_headers[0];
    }

    get LastHeader(): T {
        if (this.Length === 0) {
            throw new Error('no role header');
        }
        return this.any_headers[this.Length - 1];
    }

    get Keys(): T["Key"][] {
        return this.any_headers.map(c => c.Key);
    }

    map<U>(callbackfn: (any_header: T, index: number, array: T[]) => U): U[] {
        return this.any_headers.map(callbackfn);
    }

    mapWithAround<U>(callbackfn: (any_header: T, prev?: T | null, next?: T | null) => U): U[] {
        return this.any_headers.map((h, i, a) => callbackfn(h, a[i - 1], a[i + 1]));
    }

    hasKey(key: T["Key"]): boolean {
        return this.any_headers.find(c => c.Key === key) !== undefined;
    }

    findByKey(key: T["Key"]): T {
        const found_header = this.any_headers.find(c => c.Key === key);
        if (found_header === undefined) {
            throw new Error("no content key:" + key);
        }
        return found_header;
    }

    findIndexByKey(key: T["Key"]): number {
        const found_index = this.any_headers.findIndex(c => c.Key === key);
        if (found_index === -1) {
            throw new Error("no content key: " + key);
        }
        return found_index;
    }

    clone() {
        return this.any_headers.map(h => this.cloneHeader(h));
    }

    cloneWithReplacement(replace_fn: (any_header: T) => T | null) {
        return this.any_headers.map(
            h => {
                const replaced = replace_fn(h);
                return replaced === null ? this.cloneHeader(h) : replaced;
            }
        );
    }

    append(header: T, position: number): T[] {
        if (position < 0 || this.Length < position) {
            throw new Error(`invalid row position: ${position}`);
        }

        const new_headers: T[] = [];
        for (let i = 0; i < this.Length; ++i) {
            if (i === position) {
                new_headers.push(header)
            }
            new_headers.push(this.cloneHeader(this.any_headers[i]));
        }
        if (position === this.Length) {
            new_headers.push(header)
        }

        return new_headers;
    }

    delete(index: number): T[] {
        if (index < 0 || this.Length <= index) {
            throw new Error(`invalid row index: ${index}`);
        }

        const new_headers = this.clone();
        new_headers.splice(index, 1);
        return new_headers;
    }

    move(from_index: number, to_index: number): T[] {
        if (from_index < 0 || this.Length <= from_index) {
            throw new Error(`invalid to index: ${from_index}`);
        }
        if (to_index < 0 || this.Length <= to_index) {
            throw new Error(`invalid from index: ${to_index}`);
        }

        return array_move(this.any_headers, from_index, to_index);
    }

    toJSON() {
        return this.any_headers;
    }

}
